import React, { useState } from 'react'
import moment from 'moment';
import { useStyle } from "./Style";
import Grid from "@material-ui/core/Grid";
import {
  TextValidator,
  ValidatorForm,
  SelectValidator,
} from "react-material-ui-form-validator";
import MenuItem from "@mui/material/MenuItem";
import { getPartnerGuid } from "../../../../../lib/access.es6";

// Maps each selectable cause to its key in the claiming benefit's own
// additional_details.waiting_periods (e.g. {accidental: 0}) - mirrors
// HospitalClaimDetails.js's CAUSE_TO_WAITING_PERIOD_KEY exactly, per product's request to keep
// funeral and Hospital Cash Benefit's per-cause waiting-period UX consistent. Natural death and
// Suicide deliberately map to the same 'natural' key - Suicide is NOT treated as accidental
// (confirmed with product) and falls back to the flat, relationship-based waiting period like
// Natural death.
const CAUSE_TO_WAITING_PERIOD_KEY = {
  "Natural death": "natural",
  "Accidental death": "accidental",
  "Suicide": "natural",
};

const FuneralClaimDetails = (props) => {

  console.log("funeral claim details props", props)
  const classes = useStyle();
  const partnerGuid = getPartnerGuid();

  // The waiting period is anchored on the claiming dependant's "Cash in Lieu of Service"
  // benefit specifically - the same benefit the underwriter's own assert_waiting_period_passed
  // check reads - regardless of which funeral-family benefit the agent actually selected in
  // Step 2 (Funeral Services / Muslim Funeral / Cash in Lieu of Service can each be selected as
  // the primary benefit, but only Cash in Lieu of Service carries the canonical waiting-period
  // fields). Looked up from the full quotation schedule entry rather than
  // claimInitiationPayload.benefits, since that only contains the benefit(s) actually selected/
  // bundled, which may not include Cash in Lieu of Service at all.
  const claimingEntry = (
    (props.eventQuote && props.eventQuote.policy_schedule_entries) || []
  ).find(function (entry) {
    return (
      entry.dependant &&
      entry.dependant.guid === props.claimInitiationPayload.dependant_guid
    );
  });
  const cashInLieuBenefit =
    claimingEntry &&
    (claimingEntry.benefits || []).find(function (b) {
      return b.type === "Cash in Lieu of Service";
    });

  // Required days for causes OTHER than Accidental (which bypasses this entirely - see
  // isCauseWaitingPeriodMet below). Comes from the benefit's own additional_details.waiting_periods
  // map when present, falling back to the flat waiting_period_in_days for Natural/Suicide or
  // policies onboarded before this map existed (every currently-existing Mthunzi policy - confirmed
  // additional_details is null on every dependant's Cash in Lieu of Service benefit via the API).
  const getCauseWaitingPeriodDays = (cause) => {
    const key = CAUSE_TO_WAITING_PERIOD_KEY[cause];
    const override =
      cashInLieuBenefit &&
      cashInLieuBenefit.additional_details &&
      cashInLieuBenefit.additional_details.waiting_periods &&
      cashInLieuBenefit.additional_details.waiting_periods[key];
    if (override != null) return override;
    return cashInLieuBenefit ? cashInLieuBenefit.waiting_period_in_days : null;
  };

  const getWaitingPeriodAnchor = () =>
    (cashInLieuBenefit && cashInLieuBenefit.waiting_period_start_date) ||
    (props.eventQuote && props.eventQuote.start_date);

  // Accidental death UNCONDITIONALLY skips the elapsed-days comparison entirely - it is never
  // computed as "0 required days elapsed", because that arithmetic can still fail. Mthunzi's
  // enforced_start_date ('FirstDayOfNextMonth') means a dependant's own waiting_period_start_date
  // is routinely a few days in the FUTURE relative to today right after onboarding (confirmed live:
  // policy 3200027422's dependant had waiting_period_start_date=2026-08-01 while today was
  // 2026-07-30) - daysSince is then negative, so even "daysSince >= 0" reads false. A true bypass
  // must never run that comparison at all for Accidental, matching the underwriter's own assert
  // (an unconditional OR before the date-math) and claims-administration-dcp's _realiseWaitingPeriods.
  const isCauseWaitingPeriodMet = (cause) => {
    if (cause === "Accidental death") return true;
    const waitingDays = getCauseWaitingPeriodDays(cause);
    const anchor = getWaitingPeriodAnchor();
    if (waitingDays == null || !anchor) return true;
    const daysSince = moment().diff(moment(anchor), "days");
    return daysSince >= waitingDays;
  };

  const causeEligibleFromDate = (cause) => {
    const waitingDays = getCauseWaitingPeriodDays(cause);
    const anchor = getWaitingPeriodAnchor();
    if (waitingDays == null || !anchor) return "";
    return moment(anchor).add(waitingDays, "days").format("DD-MM-YYYY");
  };

  // Mthunzi funerals are serviced by a single home; pre-fill it (still editable).
  const OMM_DEFAULT_FUNERAL_HOME = "Mthunzi Home Ltd";
  const defaultFuneralHome =
    props.claimState.funeral_home_name ||
    (partnerGuid === "old_mutual_malawi" ? OMM_DEFAULT_FUNERAL_HOME : "");

  const [funeralHome, setFuneralHome] = useState(defaultFuneralHome);
  const [deathCertficateNumber, setDeathCertificateNumber] = useState(props.claimState.death_certficate_number|| '');
  const [cause_of_death, setCauseOfDeath] = useState(props.claimState.cause_of_death || '');
  const [burial_certificate_number, setBurialCertificateNumber] = useState(props.claimState.burial_certificate_number || '');

  React.useEffect(() => {
    props.setAdditionalState("policy_start_date", props.eventData.start_date);
  }, [props.member.date_of_birth]);

  // Push the pre-filled funeral home into the payload so it submits even if the
  // agent never edits the field.
  React.useEffect(() => {
    if (
      partnerGuid === "old_mutual_malawi" &&
      !props.claimState.funeral_home_name
    ) {
      props.setAdditionalState("funeral_home_name", OMM_DEFAULT_FUNERAL_HOME);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "cause_of_death") {
      handleSubmit(value);
    }
  };

  const handleSubmit = (value) => {
    const tempClaimInitiation = props.claimInitiationPayload;
    tempClaimInitiation["additional_details"] = {
      age: getAge(),
    };
    props.setClaimInitiationPayload(tempClaimInitiation);
  };

  const getAge = () => {
    const dob = props.member.date_of_birth;
    const dateOfDeath = props.eventDate;
    const ageAtDeath = moment(dateOfDeath).diff(moment(dob), "years");
    return ageAtDeath;
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <div className={classes.headingclaim}>
            <h4>Deceased details </h4>
          </div>
        </Grid>
      </Grid>
      <ValidatorForm onSubmit={() => {}}>
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="name"
              style={{ width: "100%" }}
              label="first_name"
              name="first_name"
              value={props.member.first_name}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="middle_name"
              style={{ width: "100%" }}
              label="middle_name"
              name="middle_name"
              value={props.member.middle_name}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="last_name"
              style={{ width: "100%" }}
              label="last_name"
              name="last_name"
              value={props.member.last_name}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="relationship"
              style={{ width: "100%" }}
              label="relationship"
              name="relationship"
              value={props.member.relationship}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="gender"
              style={{ width: "100%" }}
              label="gender"
              name="gender"
              value={props.member.gender}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="date_of_birth"
              style={{ width: "100%" }}
              label="date_of_birth"
              name="date_of_birth"
              value={props.member.date_of_birth}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="age"
              style={{ width: "100%" }}
              label="Age of Deceased"
              name="age"
              value={getAge()}
              variant="outlined"
            />
          </Grid>
        </Grid>

        <hr />

        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12} sm={4} md={4}>
            <TextValidator
              id="dateOfDeath"
              label="Date Of Death"
              name="dateOfDeath"
              value={props.eventDate}
              disabled={true}
              style={{ width: "100%" }}
              fullWidth
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
            />
          </Grid>

          {(partnerGuid === "omk" || partnerGuid === "omu_flp" || partnerGuid === "old_mutual_malawi") && (
            <Grid item xs={12} sm={4} md={4}>
              <SelectValidator
                labelId="cause-of-death"
                id="cause-of-death"
                value={cause_of_death}
                name="cause_of_death"
                type="text"
                label="Select Cause of Death"
                onChange={(e) => {
                  setCauseOfDeath(e.target.value);
                  props.setAdditionalState("cause_of_death", e.target.value);
                }}
                variant="outlined"
                validators={["required"]}
                errorMessages={["Please select cause of death"]}
                style={{ width: "100%" }}
                sx={{
                  fontFamily: "Open Sans",
                  fontStyle: "normal",
                  fontWeight: 300,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0.005em",
                  color: "#000000",
                }}
              >
                {["Natural death", "Accidental death", "Suicide"].map((cause, index) => {
                  const waitingPeriodMet = isCauseWaitingPeriodMet(cause);
                  return (
                    <MenuItem
                      key={index}
                      disabled={!waitingPeriodMet}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: "#000000",
                      }}
                      value={cause}
                    >
                      {cause}
                      {!waitingPeriodMet && (
                        <> (waiting period not met, eligible from {causeEligibleFromDate(cause)})</>
                      )}
                    </MenuItem>
                  );
                })}
              </SelectValidator>
            </Grid>
          )}

          <Grid item xs={12} sm={4} md={4}>
            <TextValidator
              id="funeralHome"
              label="Funeral Home Name"
              name="funeralHome"
              value={funeralHome}
              onChange={(e) => {
                setFuneralHome(e.target.value);
                props.setAdditionalState("funeral_home_name", e.target.value);
              }}
              style={{ width: "100%" }}
              variant="outlined"
            />
          </Grid>
          {partnerGuid !== "old_mutual_malawi" && (
          <Grid item xs={12} sm={4} md={4}>
            <TextValidator
              id="deathCertficateNumber"
              label="Death Certficate Number  "
              name="deathCertficateNumber"
              value={deathCertficateNumber}
              onChange={(e) => {
                setDeathCertificateNumber(e.target.value);
                props.setAdditionalState(
                  "death_certficate_number",
                  e.target.value
                );
              }}
              style={{ width: "100%" }}
              variant="outlined"
            />
          
          </Grid>
            )
          }
           {partnerGuid !== "old_mutual_malawi" && (
           <Grid item xs={12} sm={4} md={4}>
            <TextValidator
              id="burial_certificate_number"
              label="Burial Certificate Number"
              name="burial_certificate_number"
              value={burial_certificate_number}
              onChange={(e) => {
                setBurialCertificateNumber(e.target.value);
                props.setAdditionalState(
                  "burial_certificate_number",
                  e.target.value
                );
              }}
              style={{ width: "100%" }}
              variant="outlined"
            />
          </Grid>
           )}
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default FuneralClaimDetails

